import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, onSnapshot, query, orderBy, limit } from 'firebase/firestore';
import { CalendarDays, Clock, CheckCircle2, Wallet } from 'lucide-react';
import { db } from '@/lib/firebase';
import { formatPula } from '@/lib/money';
import type { Booking } from '@/lib/types';
import { statusBadge } from './ui';

export function Dashboard() {
  const [bookings, setBookings] = useState<Booking[]>([]);

  useEffect(() => {
    const q = query(collection(db, 'bookings'), orderBy('createdAt', 'desc'), limit(50));
    return onSnapshot(
      q,
      (snap) => setBookings(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Booking, 'id'>) }))),
      () => setBookings([]),
    );
  }, []);

  const newCount = bookings.filter((b) => b.status === 'new').length;
  const confirmed = bookings.filter((b) => b.status === 'confirmed').length;
  const pipeline = bookings
    .filter((b) => b.status === 'new' || b.status === 'confirmed')
    .reduce((s, b) => s + (b.totalPrice || 0), 0);

  const stats = [
    { label: 'New requests', value: String(newCount), icon: Clock },
    { label: 'Confirmed', value: String(confirmed), icon: CheckCircle2 },
    { label: 'Total bookings', value: String(bookings.length), icon: CalendarDays },
    { label: 'Open pipeline', value: formatPula(pipeline), icon: Wallet },
  ];

  return (
    <div>
      <h1 className="font-display text-3xl text-espresso">Dashboard</h1>
      <p className="mt-1 font-body text-sm text-muted">A quick look at what's coming in.</p>

      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((s) => (
          <div key={s.label} className="border border-line bg-paper p-5">
            <s.icon className="text-gold-deep" size={22} />
            <p className="mt-3 font-display text-3xl text-espresso">{s.value}</p>
            <p className="font-body text-xs uppercase tracking-wide text-muted">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 flex items-center justify-between">
        <h2 className="font-display text-2xl text-espresso">Recent requests</h2>
        <Link to="/admin/bookings" className="font-body text-sm text-gold-deep hover:underline">View all →</Link>
      </div>
      <div className="mt-4 overflow-hidden border border-line bg-paper">
        {bookings.slice(0, 6).map((b) => (
          <div key={b.id} className="flex items-center justify-between gap-4 border-b border-line px-5 py-3.5 last:border-0">
            <div className="min-w-0">
              <p className="truncate font-body text-sm font-semibold text-espresso">{b.fullName}</p>
              <p className="truncate font-body text-xs text-muted">{b.selectedServices?.map((s) => s.name).join(', ')}</p>
            </div>
            <div className="flex shrink-0 items-center gap-4">
              <span className="font-display text-base text-espresso">{formatPula(b.totalPrice)}</span>
              {statusBadge(b.status)}
            </div>
          </div>
        ))}
        {bookings.length === 0 && <p className="px-5 py-8 text-center font-body text-sm text-muted">No bookings yet.</p>}
      </div>
    </div>
  );
}
